import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { proxyService } from '../services/proxyService';
import { useProjects } from '../context/ProjectContext';
import { analyzeRequest, formatAnalyzeRequestMessage } from '../utils/analyzeRequest';
import {
  loadRequestAnalysisContext,
  persistAnalyzeRequestUpdates,
  readCollectionsNavFromStorage,
} from '../utils/requestAnalysisContext';

import { DFT_BRAND } from '../styles/brandPalette';

const proxyToolsCardSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  border: `1px solid ${DFT_BRAND.line}`,
  borderRadius: '14px !important',
  boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
  bgcolor: DFT_BRAND.paper,
} as const;
const proxyToolsBtnSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '0.875rem',
  height: 36,
  minHeight: 36,
  whiteSpace: 'nowrap' as const,
  boxShadow: 'none',
};

const ProxySettingsTools: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { selectedProject } = useProjects();
  const [debugMessage, setDebugMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [analyzeMessage, setAnalyzeMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);

  const requestContext = useMemo(() => {
    const fromUrl = {
      collectionId: searchParams.get('collectionId'),
      requestId: searchParams.get('requestId'),
    };
    const fromStorage = readCollectionsNavFromStorage();
    const requestId = Number(fromUrl.requestId ?? fromStorage.requestId);
    const collectionId = Number(fromUrl.collectionId ?? fromStorage.collectionId);
    return {
      requestId: Number.isFinite(requestId) ? requestId : null,
      collectionId: Number.isFinite(collectionId) ? collectionId : null,
    };
  }, [searchParams]);

  useEffect(() => {
    setAnalyzeMessage(null);
  }, [requestContext.requestId, requestContext.collectionId]);

  const collectionsLink = useMemo(() => {
    const params = new URLSearchParams();
    if (requestContext.collectionId != null) {
      params.set('collectionId', String(requestContext.collectionId));
    }
    if (requestContext.requestId != null) {
      params.set('requestId', String(requestContext.requestId));
    }
    const qs = params.toString();
    return qs ? `/collections?${qs}` : '/collections';
  }, [requestContext.collectionId, requestContext.requestId]);

  const handleDebug = async () => {
    setDebugLoading(true);
    setDebugMessage(null);
    try {
      const status = await proxyService.checkProxyStatus();
      const testResult = await proxyService.testProxyConnection();
      console.log('Proxy debug:', { status, testResult });
      setDebugMessage({
        text: `Proxy available: ${status}. Connection test: ${testResult.success ? 'SUCCESS' : 'FAILED'}. Details logged to console.`,
        type: testResult.success ? 'success' : 'error',
      });
    } catch (error: any) {
      setDebugMessage({
        text: `Debug failed: ${error?.message || 'Unknown error'}`,
        type: 'error',
      });
    } finally {
      setDebugLoading(false);
    }
  };

  const handleAnalyzeRequest = useCallback(async () => {
    if (requestContext.requestId == null) {
      setAnalyzeMessage({
        text: 'No request selected. Open a request in Collections, then return here or use Proxy Settings from that page.',
        type: 'error',
      });
      return;
    }

    setAnalyzeLoading(true);
    setAnalyzeMessage(null);
    try {
      const context = await loadRequestAnalysisContext(
        requestContext.requestId,
        selectedProject?.id ?? undefined
      );
      const result = analyzeRequest(context);
      if (result.updates.path || result.updates.body) {
        await persistAnalyzeRequestUpdates(
          context.requestId,
          { path: context.path, rawBody: context.rawBody },
          result.updates,
          selectedProject?.id ?? undefined
        );
      }
      const formatted = formatAnalyzeRequestMessage(result, {
        requestLabel: context.requestName || `Request #${context.requestId}`,
      });
      setAnalyzeMessage(formatted);
    } catch (error: any) {
      setAnalyzeMessage({
        text: `Analyze Request failed: ${error?.message || 'Unknown error'}`,
        type: 'error',
      });
    } finally {
      setAnalyzeLoading(false);
    }
  }, [requestContext.requestId, selectedProject?.id]);

  return (
    <Paper elevation={0} sx={{ ...proxyToolsCardSx, p: { xs: 1.5, sm: 2 }, mt: 0 }}>
      <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 700, color: DFT_BRAND.text }}>
        Diagnostics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Debug proxy connectivity or analyze the active collection request for common issues.
      </Typography>

      {requestContext.requestId != null ? (
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Active request: #{requestContext.requestId}
          {requestContext.collectionId != null ? ` · Collection #${requestContext.collectionId}` : ''}.{' '}
          <Link component={RouterLink} to={collectionsLink} underline="hover">
            Back to Collections
          </Link>
        </Typography>
      ) : (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Open a request in Collections to enable Analyze Request. You can still run Debug anytime.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={debugLoading ? <CircularProgress size={16} /> : <BugReportIcon />}
          onClick={handleDebug}
          disabled={debugLoading || analyzeLoading}
          sx={{
            ...proxyToolsBtnSx,
            color: '#b45309',
            borderColor: '#fcd34d',
            bgcolor: '#fffbeb',
            '&:hover': { borderColor: '#fbbf24', bgcolor: '#fef3c7' },
          }}
        >
          Debug
        </Button>
        <Button
          variant="outlined"
          color="info"
          startIcon={analyzeLoading ? <CircularProgress size={16} /> : <AnalyticsIcon />}
          onClick={handleAnalyzeRequest}
          disabled={analyzeLoading || debugLoading}
          sx={{
            ...proxyToolsBtnSx,
            color: '#0f766e',
            borderColor: '#99f6e4',
            bgcolor: '#f0fdfa',
            '&:hover': { borderColor: '#5eead4', bgcolor: '#ccfbf1' },
          }}
        >
          Analyze Request
        </Button>
      </Box>

      {debugMessage && (
        <Alert severity={debugMessage.type === 'info' ? 'info' : debugMessage.type} sx={{ mb: analyzeMessage ? 2 : 0, whiteSpace: 'pre-wrap' }}>
          {debugMessage.text}
        </Alert>
      )}
      {analyzeMessage && (
        <Alert severity={analyzeMessage.type} sx={{ whiteSpace: 'pre-wrap' }}>
          {analyzeMessage.text}
        </Alert>
      )}
    </Paper>
  );
};

export default ProxySettingsTools;
